<template>
  <div
    class="k-uploader row column no-wrap items-center justify-center rounded-20"
    :class="[{ 'k-uploader__bill': placeholderType === 'bill' }, bill ? 'q-pa-none' : 'q-pa-lg']"
  >
    <div
      v-if="!imageUrlRef.length"
      class="k-uploader__image-place-holder rounded-circle row justify-center items-center"
      :class="avatar ? 'cursor-pointer' : `${uploaderSize} rounded-12`"
      @click="onUploaderClick"
    >
      <KIcon
        :class="{ 'k-uploader__bill-placeholder-icon': bill }"
        name="add-image"
        color="cool-gray-60"
        :size="bill ? '49px' : '26px'"
      />
    </div>
    <q-img
      v-else
      :src="imagePreview"
      object-fit="cover"
      class="k-uploader__max-width-avatar rounded-circle non-selectable"
      :class="avatar ? 'cursor-pointer' : `${uploaderSize} rounded-12`"
      :placeholder-src="placeholderSrc"
      @click="singleImage && onUploaderClick()"
    />
    <div class="k-uploader__button-container row justify-center no-wrap relative-position q-mt-12 q-mb-sm q-px-12">
      <div class="k-uploader__button-upload-container absolute full-width row justify-center">
        <KActionBtn
          :label="uploaderLabelText"
          size="md"
          left-icon="camera"
          right-icon="arrow-right"
          @click="onUploaderClick"
        />
        <div
          v-if="imageUrlRef.length && deletable"
          class="k-uploader__button-delete row justify-center absolute items-center rounded-circle cursor-pointer bg-white"
          :class="deleteIconLocation"
          @click="handleDeleteImage"
        >
          <KIcon name="trash" color="error-70" size="24px" />
        </div>
      </div>
      <q-file
        v-model="imageRef"
        accept="image/jpeg, image/jpg"
        :disable="!singleImage && imageUrlRef.length >= maxItem"
        borderless
        outline
        dense
        hide-bottom-space
        bottom-slots
        :for="id"
        :max-file-size="maxImageSizeByte"
        v-bind="$attrs"
        @update:model-value="handleUpload"
        @rejected="onRejected"
      />
    </div>

    <span v-if="!errorRef" class="fs-13px fw-400 text-cool-gray-60" :class="{ 'q-mb-12': !bill }">
      {{ text.imageHint }}
    </span>
    <slot v-else name="error">
      <span class="fs-13px fw-400 text-error-100 text-center" :class="{ 'q-mb-12': !bill }">
        {{ text.errorMessage }}
      </span>
    </slot>
    <KUploaderCarousel
      v-if="!singleImage && imageUrlRef.length > 1"
      v-model="imageUrlRef"
      v-model:sort="sortRef"
      v-model:active-image="imagePreviewIndexRef"
      :images="imageUrlRef"
    />
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue'
import KUploaderCarousel from './KUploaderCarousel.vue'

defineOptions({ name: 'KUploader', inheritAttrs: true })

const modelValue = defineModel({ type: Array, required: true })
const sort = defineModel('sort', { type: Array, default: () => [] })
const newImage = defineModel('newImage', { type: Array, default: () => [] })

const props = defineProps({
  placeholderType: { type: String, default: 'avatar', validator: (v) => ['avatar', 'item', 'bill'] },
  placeholderSrc: { type: String, default: '' },
  maxItem: { type: Number, default: 6 },
  id: { type: String, required: true }, // must be a unique id
  maxImageSize: { type: Number, default: 0.8 },
  text: { type: Object, default: () => {} },
  deletable: { type: Boolean, default: true },
})

const emits = defineEmits(['on-delete'])

const imageRef = ref(null)
const imageUrlRef = ref(modelValue.value)
const errorRef = ref(false)
const imagePreviewIndexRef = ref(0)
const sortRef = ref(sort.value)
const newImageRef = ref(newImage.value)

const avatar = computed(() => {
  return props.placeholderType === 'avatar'
})

const bill = computed(() => {
  return props.placeholderType === 'bill'
})

const singleImage = computed(() => {
  if (props.placeholderType === 'avatar' || props.maxItem === 1 || bill.value) return true
  else return false
})

const uploaderSize = computed(() => {
  if (props.placeholderType === 'item') return 'k-uploader__max-width-item'
  else return 'k-uploader__max-width-bill'
})

const deleteIconLocation = computed(() => {
  if (props.placeholderType === 'avatar') return 'k-uploader__button-delete--avatar'
  else if (props.placeholderType === 'bill') return 'k-uploader__button-delete--bill'
  else return 'k-uploader__button-delete--two-items'
})

const maxImageSizeByte = computed(() => {
  return props.maxImageSize * 1000000
})

const imagePreview = computed(() => {
  if (imageUrlRef.value.length) {
    if (typeof imageUrlRef.value[imagePreviewIndexRef.value] === 'string')
      return imageUrlRef.value[imagePreviewIndexRef.value]
    else return URL.createObjectURL(imageUrlRef.value[imagePreviewIndexRef.value])
  }
  return imageUrlRef.value[imagePreviewIndexRef.value]
})

const uploaderLabelText = computed(() => {
  if (avatar.value) return imageUrlRef.value.length === 0 ? props.text.avatar.single : props.text.avatar.multiple
  else if (props.maxItem === 1 || bill.value) return props.text.item.single
  else return imageUrlRef.value.length === 0 ? props.text.item.single : props.text.item.multiple
})

const handleUpload = () => {
  errorRef.value = false

  if (sortRef.value.includes(-1)) {
    sortRef.value = []
    sort.value = []
  }

  if (imageRef.value) {
    if (!avatar.value && props.maxItem > 1 && !bill.value) {
      const largestSortNumber = sort.value.length ? Math.max(...sort.value) : 0
      const localSort = largestSortNumber + 1
      sortRef.value.push(localSort)
      imageUrlRef.value.push(imageRef.value)
      imagePreviewIndexRef.value = imageUrlRef.value.length - 1
      modelValue.value = imageUrlRef.value
      sort.value = sortRef.value
    } else {
      sortRef.value = [1]
      imageUrlRef.value = [imageRef.value]
      modelValue.value = imageUrlRef.value
      sort.value = sortRef.value
    }
    imageRef.value = null
    GenerateNewImage()
  }
}

const onUploaderClick = () => {
  document.querySelector(`label[for="${props.id}"]`).click()
}

const handleDeleteImage = () => {
  if (avatar.value) {
    imageUrlRef.value.splice(0)
    modelValue.value = imageUrlRef.value
    GenerateNewImage()
    newImageRef.value.splice(0)
    sortRef.value = [-1]
    sort.value = sortRef.value
    newImage.value = newImageRef.value
  } else {
    const index = imagePreviewIndexRef.value
    imagePreviewIndexRef.value = 0
    imageUrlRef.value.splice(index, 1)
    modelValue.value = imageUrlRef.value
    sortRef.value.splice(index, 1)

    if (!sortRef.value.length) sortRef.value = [-1]
    sort.value = sortRef.value

    GenerateNewImage()
  }

  emits('on-delete')
}

const GenerateNewImage = () => {
  newImageRef.value = []
  const images = []

  const localNewImages = imageUrlRef.value

  for (let i = 0; i < localNewImages.length; i++)
    if (typeof localNewImages[i] !== 'string') images.push(localNewImages[i])

  newImageRef.value = [...images]
  newImage.value = newImageRef.value
}

const onRejected = () => {
  errorRef.value = true
}
watchEffect(() => {
  imageUrlRef.value = modelValue.value
})
</script>

<style lang="sass" scoped>
.k-uploader
  &:not(.k-uploader__bill)
    min-width: 388px
    max-width: 388px
  &__bill
    width: 100%
    padding-left: none !important
    padding-right: none !important
  &__image-place-holder
    border: 1.5px dashed var(--cool-gray-40)
    width: 120px
    height: 120px
  &__max-width-avatar
    width: 120px
    height: 120px
    border: 1.5px solid var(--cool-gray-30)
  &__max-width-item
    width: 188px
    height: 120px
  &__max-width-bill
    width: 100%
    height: 136px
  &__button-container
    min-width: 85%
  &__button-upload-container
    top: 0
    z-index: 10
  &__button-delete
    border: 1.5px solid var(--cool-gray-40)
    &--avatar
      width: 32px
      height: 32px
      top: -120px
      right: 74px
    &--bill
      width: 32px
      height: 32px
      top: -137px
      right: -9px
    &--two-items
      width: 32px
      height: 32px
      top: -125px
      right: 60px
  &__bill-placeholder-icon
    margin-top: -5px
</style>
